import { type ReactNode, useEffect, useEffectEvent } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import type { ParameterGroup } from "../generated/api/types.gen";
import { useParameterMutations } from "../hooks/useParameterMutations";
import {
  getButtonLabel,
  isRegenerateDisabled,
  type ProcessingState,
} from "../lib/parameter-panel-helpers";
import { Button } from "./Button";
import { LoadingSpinner } from "./LoadingSpinner";
import {
  ParameterFormProvider,
  useParameterForm,
} from "./ParameterFormContext";
import { ParameterGroupItem } from "./ParameterGroupItem";

export interface ParameterPanelState {
  formParameterGroups: ParameterGroup[];
  originalParameterGroups?: ParameterGroup[];
  /** Field-level dirty flag for the `parameter_groups` field only. Do NOT use
   *  the form-level `isDirty` here — that flips true for any dirty field
   *  (e.g. reference images) and would wrongly enable "Refine Further". */
  isParametersDirty: boolean;
  promptDirty: boolean;
  /** A reference image is attached. Counts as a change on its own: attaching a
   *  reference with an untouched prompt and parameters is a valid refinement. */
  hasReferenceImages: boolean;
  processing: Pick<
    ProcessingState,
    "isRegenerating" | "isInitialLoading" | "isAttachingImage"
  >;
  onParameterChange: () => void;
  onRegenerateImages: () => void;
  /** Project the refined image belongs to; floats project assets to the top
   *  of the color picker's Assets tab. */
  projectId?: string | null;
  /** True while auto parameter values are being re-read from the freshly
   *  rendered refinement image. */
  isResyncingParameters: boolean;
}

interface ParameterPanelProps {
  imageId: string;
  shouldAutoExtract: boolean;
  onExtractionStateChange: (isExtracting: boolean) => void;
  /** The drawer's collapse toggle, placed at the end of the header. */
  collapseToggle: ReactNode;
  panel: ParameterPanelState;
}

function useRegenerateHotkey(
  processing: ProcessingState,
  hasChanges: boolean,
  onRegenerateImages: () => void,
) {
  useHotkeys(
    "Enter",
    (e) => {
      // An open modal (e.g. add-asset) owns Enter while it is up.
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      if (isRegenerateDisabled(processing, hasChanges)) return;
      onRegenerateImages();
    },
    { enableOnFormTags: ["input", "textarea"] },
  );
}

function ParameterExtractionState({
  isExtracting,
  errorMessage,
  onRetry,
}: {
  isExtracting: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}) {
  if (isExtracting) {
    return (
      <div className="mx-6 mb-4 p-4 bg-[var(--accent-primary)] rounded-lg border border-[var(--accent-primary)]">
        <LoadingSpinner text="Extracting parameters from image..." size="sm" />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="mx-6 mb-4 p-4 bg-[var(--accent-destructive)] rounded-lg border border-[var(--stroke-destructive)]">
        <p className="text-sm text-[var(--text-primary)] mb-3">
          Failed to extract parameters: {errorMessage}
        </p>
        <Button onClick={onRetry} disabled={isExtracting} className="w-full">
          Retry Extraction
        </Button>
      </div>
    );
  }

  return null;
}

function ExpandErrors({
  groupErrorMessage,
  isExpandingGroup,
  elementsErrorMessage,
  isExpandingElements,
}: {
  groupErrorMessage: string | null;
  isExpandingGroup: boolean;
  elementsErrorMessage: string | null;
  isExpandingElements: boolean;
}) {
  return (
    <>
      {groupErrorMessage && !isExpandingGroup && (
        <div className="mb-3 p-3 bg-[var(--accent-destructive)] rounded-lg border border-[var(--stroke-destructive)]">
          <p className="text-xs text-[var(--text-primary)]">
            Failed to expand group: {groupErrorMessage}
          </p>
        </div>
      )}

      {elementsErrorMessage && !isExpandingElements && (
        <div className="mb-3 p-3 bg-[var(--accent-destructive)] rounded-lg border border-[var(--stroke-destructive)]">
          <p className="text-xs text-[var(--text-primary)]">
            Failed to add elements: {elementsErrorMessage}
          </p>
        </div>
      )}
    </>
  );
}

function RegenerateButton({
  processing,
  onRegenerateImages,
}: {
  processing: ProcessingState;
  onRegenerateImages: () => void;
}) {
  const { hasChanges } = useParameterForm();
  const isDisabled = isRegenerateDisabled(processing, hasChanges);

  return (
    <div className="pt-2">
      <Button
        onClick={onRegenerateImages}
        disabled={isDisabled}
        className={`w-full py-3 ${!hasChanges ? "opacity-35 cursor-not-allowed" : ""}`}
      >
        {getButtonLabel(processing)}
      </Button>
      {!(
        hasChanges ||
        processing.isRegenerating ||
        processing.isInitialLoading ||
        processing.isExtractingParameters ||
        processing.isExpandingGroup ||
        processing.isExpandingElements ||
        processing.isResyncingParameters
      ) && (
        <p className="mt-2 text-sm text-[var(--text-secondary)] text-center">
          Make a parameter change or request to refine your image further.
        </p>
      )}
    </div>
  );
}

function ParameterPanelHeader({
  isExpandDisabled,
  isExpandingElements,
  onExpandElements,
  collapseToggle,
}: {
  isExpandDisabled: boolean;
  isExpandingElements: boolean;
  onExpandElements: () => void;
  collapseToggle: ReactNode;
}) {
  return (
    <div className="px-6 pt-6 pb-9">
      <div className="flex h-12 items-center gap-3">
        <h2 className="text-heading-modal">Refine</h2>
        <Button
          variant="toolbar"
          onClick={onExpandElements}
          disabled={isExpandDisabled}
        >
          {isExpandingElements ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Finding elements...</span>
            </>
          ) : (
            <span>Add Components</span>
          )}
        </Button>
        <div className="ml-auto flex">{collapseToggle}</div>
      </div>
    </div>
  );
}

function ParameterGroupList({
  formParameterGroups,
  originalParameterGroups,
  isExpandingGroup,
  expandingGroupName,
}: {
  formParameterGroups: ParameterGroup[];
  originalParameterGroups?: ParameterGroup[];
  isExpandingGroup: boolean;
  expandingGroupName: string | null;
}) {
  if (!formParameterGroups?.length) {
    return null;
  }

  return (
    <div className="space-y-4">
      {formParameterGroups.map((group, groupIndex) => (
        <ParameterGroupItem
          key={group.group_name}
          group={group}
          groupIndex={groupIndex}
          originalParameterGroups={originalParameterGroups}
          isExpandingThisGroup={
            isExpandingGroup && expandingGroupName === group.group_name
          }
          defaultCollapsed={groupIndex > 1}
        />
      ))}
    </div>
  );
}

function BottomSection({
  processing,
  expandGroupErrorMessage,
  expandElementsErrorMessage,
  onRegenerateImages,
}: {
  processing: ProcessingState;
  expandGroupErrorMessage: string | null;
  expandElementsErrorMessage: string | null;
  onRegenerateImages: () => void;
}) {
  return (
    <div className="px-6 pb-4 pt-2 bg-[var(--bg-modal)]">
      <ExpandErrors
        groupErrorMessage={expandGroupErrorMessage}
        isExpandingGroup={processing.isExpandingGroup}
        elementsErrorMessage={expandElementsErrorMessage}
        isExpandingElements={processing.isExpandingElements}
      />

      <RegenerateButton
        processing={processing}
        onRegenerateImages={onRegenerateImages}
      />
    </div>
  );
}

function useParameterPanelState(
  panel: ParameterPanelState,
  mutations: ReturnType<typeof useParameterMutations>,
) {
  const {
    formParameterGroups,
    originalParameterGroups,
    isParametersDirty: isDirty,
    promptDirty,
    hasReferenceImages,
    processing: propsProcessing,
    onParameterChange,
    onRegenerateImages,
    projectId,
    isResyncingParameters,
  } = panel;
  const processing: ProcessingState = {
    ...propsProcessing,
    ...mutations.processing,
    isResyncingParameters,
  };
  const hasChanges = isDirty || promptDirty || hasReferenceImages;

  useRegenerateHotkey(processing, hasChanges, onRegenerateImages);

  const isFormDisabled =
    processing.isRegenerating ||
    processing.isInitialLoading ||
    promptDirty ||
    processing.isExtractingParameters ||
    processing.isExpandingGroup ||
    processing.isExpandingElements;
  const isExpandDisabled = isFormDisabled || !(formParameterGroups?.length > 0);

  return {
    formParameterGroups,
    originalParameterGroups,
    promptDirty,
    onParameterChange,
    onRegenerateImages,
    isDirty,
    hasChanges,
    mutations,
    processing,
    isFormDisabled,
    isExpandDisabled,
    projectId,
  };
}

export function ParameterPanel({
  imageId,
  shouldAutoExtract,
  onExtractionStateChange,
  collapseToggle,
  panel,
}: ParameterPanelProps) {
  const mutations = useParameterMutations(imageId);
  const isExtracting = mutations.processing.isExtractingParameters;
  const triggerAutoExtract = useEffectEvent((_targetImageId: string) => {
    mutations.extractParameters();
  });

  useEffect(() => {
    if (!shouldAutoExtract) return;
    triggerAutoExtract(imageId);
  }, [shouldAutoExtract, imageId]);

  useEffect(() => {
    onExtractionStateChange(isExtracting);
  }, [isExtracting, onExtractionStateChange]);

  const s = useParameterPanelState(panel, mutations);

  return (
    <ParameterFormProvider
      value={{
        isDirty: s.isDirty,
        promptDirty: s.promptDirty,
        hasChanges: s.hasChanges,
        isFormDisabled: s.isFormDisabled,
        isExpandDisabled: s.isExpandDisabled,
        onParameterChange: s.onParameterChange,
        onExpandGroup: s.mutations.expandGroupParameters,
        expandingGroupName: s.mutations.expandingGroupName,
        isExpandingGroup: s.processing.isExpandingGroup,
        isResyncingParameters: panel.isResyncingParameters,
        projectId: s.projectId,
        formParameterGroups: s.formParameterGroups,
      }}
    >
      <div className="flex flex-col h-full bg-[var(--bg-modal)]">
        <ParameterPanelHeader
          isExpandDisabled={s.isExpandDisabled}
          isExpandingElements={s.processing.isExpandingElements}
          onExpandElements={s.mutations.expandElements}
          collapseToggle={collapseToggle}
        />
        <ParameterExtractionState
          isExtracting={isExtracting}
          errorMessage={mutations.extractionErrorMessage}
          onRetry={mutations.extractParameters}
        />
        <div className="scrollbar-hide flex-1 overflow-y-auto px-6 pb-6">
          <ParameterGroupList
            formParameterGroups={s.formParameterGroups}
            originalParameterGroups={s.originalParameterGroups}
            isExpandingGroup={s.processing.isExpandingGroup}
            expandingGroupName={mutations.expandingGroupName}
          />
        </div>
        <BottomSection
          processing={s.processing}
          expandGroupErrorMessage={mutations.expandGroupErrorMessage}
          expandElementsErrorMessage={mutations.expandElementsErrorMessage}
          onRegenerateImages={s.onRegenerateImages}
        />
      </div>
    </ParameterFormProvider>
  );
}
